/**
 * interactive-deduction (Phase 3-3, Task 6.3) EvidenceSelector atom。
 *
 * プレイヤー手札の MemoryFragment 一覧から「言弾」を選ぶ UI。
 * `isEvidence: true` の fragment は視覚的に強調 (バッジ + 明るい枠) する。
 * tap で親に key を通知する。
 */

export type EvidenceFragment = {
  key: string;
  label?: string;
  isEvidence?: boolean;
  evidenceTag?: string[];
};

export type EvidenceSelectorProps = {
  fragments: EvidenceFragment[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  disabled?: boolean | undefined;
};

export function EvidenceSelector({
  fragments,
  selectedKey,
  onSelect,
  disabled = false,
}: EvidenceSelectorProps): JSX.Element {
  if (fragments.length === 0) {
    return (
      <p className="text-sm text-slate-400" data-testid="evidence-empty">
        提示できる記憶がありません。
      </p>
    );
  }

  return (
    <ul
      className="flex flex-col gap-2"
      data-testid="evidence-selector"
      role="radiogroup"
      aria-label="言弾として提示する記憶を選択"
    >
      {fragments.map((f) => {
        const isSelected = f.key === selectedKey;
        const isHighlighted = f.isEvidence === true;
        return (
          <li key={f.key}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => onSelect(f.key)}
              data-testid={`evidence-item-${f.key}`}
              data-evidence={isHighlighted ? "true" : "false"}
              className={[
                "w-full text-left px-3 py-2 rounded border transition-colors",
                isSelected
                  ? "border-indigo-400 bg-indigo-900/40"
                  : "border-slate-700 bg-slate-800/60 hover:bg-slate-700/60",
                isHighlighted ? "ring-1 ring-amber-400/60" : "",
                disabled ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <span className="flex items-center gap-2">
                {isHighlighted ? (
                  <span
                    className="text-[10px] uppercase tracking-wide bg-amber-500/90 text-slate-900 px-1.5 py-0.5 rounded font-bold"
                    data-testid="evidence-badge"
                  >
                    証拠
                  </span>
                ) : null}
                <span className="text-sm text-slate-100">
                  {f.label ?? f.key}
                </span>
              </span>
              {f.evidenceTag && f.evidenceTag.length > 0 ? (
                <span className="block mt-1 text-[10px] text-slate-400">
                  {f.evidenceTag.join(" / ")}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
